const DELIVERY_CONFIG_STORAGE_KEY = 'admin_delivery_radius_config';

export const DEFAULT_DELIVERY_CONFIG = {
  products: {
    visibilityRadiusKm: 5,
    featuredProductsLimit: 8,
    freeDeliveryThreshold: 200,
    deliveryCharge: 30
  },
  packages: {
    packageTypes: ['Documents', 'Groceries', 'Electronics', 'Fragile'],
    baseFee: 30,
    perKmFee: 8,
    maxDistanceKm: 30,
    mapRadiusMeters: 50000
  },
  deliveryPartners: {
    orderRadiusKm: 3.0,
    packageRadiusKm: 5.0
  }
};

const toNumberOrFallback = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizePackageTypes = (value) => {
  if (Array.isArray(value)) {
    const items = value
      .map((item) => String(item || '').trim())
      .filter(Boolean);

    return items.length ? items : DEFAULT_DELIVERY_CONFIG.packages.packageTypes;
  }

  if (typeof value === 'string') {
    const items = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    return items.length ? items : DEFAULT_DELIVERY_CONFIG.packages.packageTypes;
  }

  return DEFAULT_DELIVERY_CONFIG.packages.packageTypes;
};

export const normalizeDeliveryConfig = (config = {}) => ({
  products: {
    visibilityRadiusKm: toNumberOrFallback(
      config?.products?.visibilityRadiusKm,
      DEFAULT_DELIVERY_CONFIG.products.visibilityRadiusKm
    ),
    featuredProductsLimit: toNumberOrFallback(
      config?.products?.featuredProductsLimit,
      DEFAULT_DELIVERY_CONFIG.products.featuredProductsLimit
    ),
    freeDeliveryThreshold: toNumberOrFallback(
      config?.products?.freeDeliveryThreshold,
      DEFAULT_DELIVERY_CONFIG.products.freeDeliveryThreshold
    ),
    deliveryCharge: toNumberOrFallback(
      config?.products?.deliveryCharge,
      DEFAULT_DELIVERY_CONFIG.products.deliveryCharge
    )
  },
  packages: {
    packageTypes: normalizePackageTypes(config?.packages?.packageTypes),
    baseFee: toNumberOrFallback(
      config?.packages?.baseFee,
      DEFAULT_DELIVERY_CONFIG.packages.baseFee
    ),
    perKmFee: toNumberOrFallback(
      config?.packages?.perKmFee,
      DEFAULT_DELIVERY_CONFIG.packages.perKmFee
    ),
    maxDistanceKm: toNumberOrFallback(
      config?.packages?.maxDistanceKm,
      DEFAULT_DELIVERY_CONFIG.packages.maxDistanceKm
    ),
    mapRadiusMeters: toNumberOrFallback(
      config?.packages?.mapRadiusMeters,
      DEFAULT_DELIVERY_CONFIG.packages.mapRadiusMeters
    )
  },
  deliveryPartners: {
    orderRadiusKm: toNumberOrFallback(
      config?.deliveryPartners?.orderRadiusKm,
      DEFAULT_DELIVERY_CONFIG.deliveryPartners.orderRadiusKm
    ),
    packageRadiusKm: toNumberOrFallback(
      config?.deliveryPartners?.packageRadiusKm,
      DEFAULT_DELIVERY_CONFIG.deliveryPartners.packageRadiusKm
    )
  }
});

export const getStoredDeliveryConfig = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_DELIVERY_CONFIG;
  }

  try {
    const rawValue = window.localStorage.getItem(DELIVERY_CONFIG_STORAGE_KEY);
    if (!rawValue) {
      return DEFAULT_DELIVERY_CONFIG;
    }

    return normalizeDeliveryConfig(JSON.parse(rawValue));
  } catch (error) {
    console.error('Failed to read delivery config from localStorage:', error);
    return DEFAULT_DELIVERY_CONFIG;
  }
};

export const saveStoredDeliveryConfig = (config) => {
  const normalizedConfig = normalizeDeliveryConfig(config);

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(
        DELIVERY_CONFIG_STORAGE_KEY,
        JSON.stringify(normalizedConfig)
      );
    } catch (error) {
      console.error('Failed to save delivery config to localStorage:', error);
    }
  }

  return normalizedConfig;
};
